import { useEffect, useRef, useState } from 'react';
import { useSnackbar } from 'notistack';
import { useAssetSettings } from '@nsd/fe';
import { useAppInsightsContext, useTrackEvent } from '@microsoft/applicationinsights-react-js';
import { five9SingleTabLock } from 'src/constants/featureIdentifiers';
import five9Logger from 'src/utils/five9/five9Logger';
import isTruthyValue from 'src/utils/isTruthyValue';

/**
 * @typedef {import('src/type-defs/five9Types').Five9LockMessage} Five9LockMessage
 */

/**
 * @typedef {Object} Five9SingleTabLockOptions
 * @property {boolean} [enabled=true] Turns the single-tab election on or off.
 */

/**
 * @typedef {'claim'|'heartbeat'|'release'|'takeover'} LockMessageTypeValue
 */

const CHANNEL_NAME = 'five9-adt-lock';

const LockMessageType = Object.freeze({
  CLAIM: 'claim',
  HEARTBEAT: 'heartbeat',
  RELEASE: 'release',
  TAKEOVER: 'takeover'
});

const HEARTBEAT_INTERVAL_MS = 2000;
const STALE_LOCK_MS = 8000;
const PROBE_WINDOW_MS = 500;

/**
 * Enforces a single active Five9 tab and exposes takeover behavior for conflict recovery.
 *
 * @param {Five9SingleTabLockOptions} [options]
 * @returns {{ isBlocked: boolean, isReady: boolean, requestTakeover: () => void }}
 */
export default function useFive9SingleTabLock({ enabled = true } = {}) {
  const { hasFeatureAccess } = useAssetSettings();
  const envEnabled = isTruthyValue(process.env.REACT_APP_ENABLE_FIVE9_SINGLE_TAB_LOCK);
  const isLockEnabled = enabled && (envEnabled || !!hasFeatureAccess(five9SingleTabLock));
  const { enqueueSnackbar } = useSnackbar();
  const appInsights = useAppInsightsContext();
  const trackMultiTabWarning = useTrackEvent(
    appInsights,
    'DispatchQoreFE::Five9::MultiTabWarning',
    null
  );

  const [isBlocked, setIsBlocked] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const warningIssuedRef = useRef(false);
  const requestTakeoverRef = useRef(() => {});

  useEffect(() => {
    if (!enabled) {
      requestTakeoverRef.current = () => {};
      setIsBlocked(false);
      setIsReady(true);
      return undefined;
    }

    if (!isLockEnabled) {
      requestTakeoverRef.current = () => {};
      setIsReady(true);
      if (typeof BroadcastChannel === 'undefined') return undefined;

      const channel = new BroadcastChannel(CHANNEL_NAME);
      const emitWarning = () => {
        if (warningIssuedRef.current) return;
        warningIssuedRef.current = true;
        trackMultiTabWarning({ reason: 'anotherTabDetected' });
        five9Logger.warn('five9.secondPublisherStandDown', { reason: 'anotherTabDetected' });
        enqueueSnackbar(
          'Five9 may already be open in another tab. Using multiple tabs can cause dropped calls.',
          { variant: 'warning' }
        );
      };

      const handleMessage = (event) => {
        const messageType = event?.data?.type;
        if (messageType === LockMessageType.CLAIM || messageType === LockMessageType.HEARTBEAT) {
          emitWarning();
        }
      };

      channel.addEventListener('message', handleMessage);
      channel.postMessage({ type: LockMessageType.CLAIM, sessionId: 'warn-only', timestamp: Date.now() });

      return () => {
        channel.removeEventListener('message', handleMessage);
        channel.close();
      };
    }

    if (typeof BroadcastChannel === 'undefined') {
      requestTakeoverRef.current = () => {};
      setIsReady(true);
      return undefined;
    }

    const sessionId = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    const channel = new BroadcastChannel(CHANNEL_NAME);
    let role = 'probing';
    let lastForeignHeartbeat = 0;
    let heartbeatTimer = null;
    let probeTimer = null;

    const send = (type) => {
      try {
        channel.postMessage({ type, sessionId, timestamp: Date.now() });
      } catch (_) {
        // The channel can close while the lock is tearing down.
      }
    };

    const becomeOwner = () => {
      if (role === 'owner') return;
      role = 'owner';
      setIsBlocked(false);
      setIsReady(true);
      send(LockMessageType.HEARTBEAT);
      if (heartbeatTimer) clearInterval(heartbeatTimer);
      heartbeatTimer = setInterval(() => send(LockMessageType.HEARTBEAT), HEARTBEAT_INTERVAL_MS);
    };

    const beginProbe = () => {
      role = 'probing';
      lastForeignHeartbeat = 0;
      send(LockMessageType.CLAIM);
      if (probeTimer) clearTimeout(probeTimer);
      probeTimer = setTimeout(() => {
        probeTimer = null;
        becomeOwner();
      }, PROBE_WINDOW_MS);
    };

    const becomeFollower = (reason) => {
      if (role === 'follower') return;
      const wasOwner = role === 'owner';
      role = 'follower';

      if (heartbeatTimer) {
        clearInterval(heartbeatTimer);
        heartbeatTimer = null;
      }

      if (!wasOwner) {
        trackMultiTabWarning({ reason: reason || 'lockClaimedByOtherTab' });
      }

      five9Logger.warn('five9.secondPublisherStandDown', {
        reason: reason || 'lockClaimedByOtherTab',
        wasOwner
      });

      setIsBlocked(true);
      setIsReady(true);
    };

    const handleMessage = (event) => {
      const msg = event?.data;
      if (!msg || msg.sessionId === sessionId) return;

      const ts = msg.timestamp || Date.now();

      if (role === 'probing') {
        if (msg.type === LockMessageType.RELEASE) return;
        if (msg.type === LockMessageType.CLAIM && msg.sessionId > sessionId) return;

        lastForeignHeartbeat = ts;
        if (probeTimer) {
          clearTimeout(probeTimer);
          probeTimer = null;
        }
        becomeFollower('lockHeldByExistingTab');
        return;
      }

      if (role === 'owner') {
        if (msg.type === LockMessageType.CLAIM) {
          send(LockMessageType.HEARTBEAT);
        } else if (msg.type === LockMessageType.TAKEOVER) {
          send(LockMessageType.RELEASE);
          becomeFollower('takeoverRequestedByPeerTab');
        }
        return;
      }

      if (msg.type === LockMessageType.CLAIM || msg.type === LockMessageType.HEARTBEAT) {
        lastForeignHeartbeat = ts;
      } else if (msg.type === LockMessageType.RELEASE) {
        beginProbe();
      }
    };

    requestTakeoverRef.current = () => {
      if (role === 'owner') return;
      beginProbe();
      send(LockMessageType.TAKEOVER);
    };

    channel.addEventListener('message', handleMessage);
    beginProbe();

    const staleCheckTimer = setInterval(() => {
      if (role !== 'follower') return;
      const now = Date.now();
      if (lastForeignHeartbeat && (now - lastForeignHeartbeat) > STALE_LOCK_MS) {
        beginProbe();
      }
    }, HEARTBEAT_INTERVAL_MS);

    const handleBeforeUnload = () => {
      if (role === 'owner') send(LockMessageType.RELEASE);
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      requestTakeoverRef.current = () => {};
      window.removeEventListener('beforeunload', handleBeforeUnload);
      if (heartbeatTimer) clearInterval(heartbeatTimer);
      if (probeTimer) clearTimeout(probeTimer);
      clearInterval(staleCheckTimer);
      channel.removeEventListener('message', handleMessage);
      if (role === 'owner') send(LockMessageType.RELEASE);
      channel.close();
    };
  }, [isLockEnabled]);

  return {
    isBlocked,
    isReady,
    requestTakeover: () => requestTakeoverRef.current()
  };
}
